import { supabase } from "@/integrations/supabase/client";

type RequestRow = {
  id: string;
  parent_id: string;
  title: string;
  city: string | null;
  proposed_rate: number | null;
};

type ProfileRow = {
  user_id: string;
  full_name: string | null;
  latitude: number | null;
  longitude: number | null;
};

let observer: MutationObserver | null = null;
let scheduled = false;
let loaded = false;
let requests: RequestRow[] = [];
let profiles = new Map<string, ProfileRow>();
let currentPosition: { lat: number; lng: number } | null = null;

const norm = (v: string | null | undefined) => (v || "").trim().toLocaleLowerCase("fr-FR");

function haversineKm(aLat: number, aLng: number, bLat: number, bLng: number) {
  const R = 6371;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(bLat - aLat);
  const dLng = toRad(bLng - aLng);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

async function loadData() {
  const { data: reqData } = await supabase
    .from("care_requests")
    .select("id,parent_id,title,city,proposed_rate")
    .order("created_at", { ascending: false });
  requests = (reqData as RequestRow[] | null) || [];

  const ids = [...new Set(requests.map((r) => r.parent_id))];
  profiles = new Map();
  if (ids.length) {
    const { data: profileData } = await supabase
      .from("profiles")
      .select("user_id,full_name,latitude,longitude")
      .in("user_id", ids);
    (profileData as ProfileRow[] | null)?.forEach((p) => profiles.set(p.user_id, p));
  }
  loaded = true;
}

function findPageRoot() {
  const heading = Array.from(document.querySelectorAll<HTMLElement>("h1,h2,h3")).find((el) => (el.textContent || "").trim() === "Annonces");
  if (!heading) return null;
  const root = heading.closest<HTMLElement>("main") || heading.parentElement?.parentElement || document.body;
  return { heading, root };
}

function findCardForRequest(root: HTMLElement, req: RequestRow) {
  const profile = profiles.get(req.parent_id);
  const candidates = Array.from(root.querySelectorAll<HTMLElement>("div,article"));
  return candidates
    .filter((el) => {
      const text = norm(el.textContent);
      if (!text.includes(norm(req.title))) return false;
      if (req.city && !text.includes(norm(req.city))) return false;
      if (profile?.full_name && !text.includes(norm(profile.full_name))) return false;
      return el.className.includes("rounded") && (el.className.includes("border") || el.className.includes("shadow"));
    })
    .sort((a, b) => a.textContent!.length - b.textContent!.length)[0] || null;
}

function buildControls(heading: HTMLElement, root: HTMLElement) {
  let controls = document.getElementById("cg-public-request-filters") as HTMLDivElement | null;
  if (controls) return controls;

  const cities = Array.from(new Set(requests.map((r) => r.city).filter(Boolean) as string[])).sort((a, b) => a.localeCompare(b, "fr", { sensitivity: "base" }));

  controls = document.createElement("div");
  controls.id = "cg-public-request-filters";
  controls.className = "my-4 rounded-xl border bg-card p-3 sm:p-4 shadow-sm";
  controls.innerHTML = `
    <div class="mb-2 text-sm font-semibold">🔎 Filtrer les annonces</div>
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-2">
      <input data-name type="text" placeholder="Nom du parent" class="h-10 rounded-md border border-input bg-background px-3 text-sm" />
      <select data-city class="h-10 rounded-md border border-input bg-background px-3 text-sm">
        <option value="all">Toutes les villes</option>
        ${cities.map((c) => `<option value="${norm(c).replace(/"/g, "&quot;")}">${c}</option>`).join("")}
      </select>
      <input data-rate-min type="number" min="0" step="0.5" placeholder="Tarif min €/h" class="h-10 rounded-md border border-input bg-background px-3 text-sm" />
      <input data-rate-max type="number" min="0" step="0.5" placeholder="Tarif max €/h" class="h-10 rounded-md border border-input bg-background px-3 text-sm" />
      <select data-radius class="h-10 rounded-md border border-input bg-background px-3 text-sm">
        <option value="off">Distance autour de moi</option>
        <option value="10">Dans un rayon de 10 km</option>
        <option value="25">Dans un rayon de 25 km</option>
        <option value="50">Dans un rayon de 50 km</option>
        <option value="100">Dans un rayon de 100 km</option>
      </select>
      <button data-reset type="button" class="h-10 rounded-md border px-3 text-sm font-medium hover:bg-muted">Réinitialiser</button>
    </div>
    <div data-status class="mt-2 text-xs text-muted-foreground"></div>
  `;

  const anchor = heading.parentElement || heading;
  anchor.insertAdjacentElement("afterend", controls);

  const apply = async () => {
    const name = norm(controls!.querySelector<HTMLInputElement>("[data-name]")?.value);
    const city = controls!.querySelector<HTMLSelectElement>("[data-city]")?.value || "all";
    const minRaw = controls!.querySelector<HTMLInputElement>("[data-rate-min]")?.value || "";
    const maxRaw = controls!.querySelector<HTMLInputElement>("[data-rate-max]")?.value || "";
    const min = minRaw === "" ? null : Number(minRaw);
    const max = maxRaw === "" ? null : Number(maxRaw);
    const radiusRaw = controls!.querySelector<HTMLSelectElement>("[data-radius]")?.value || "off";
    const radius = radiusRaw === "off" ? null : Number(radiusRaw);

    if (radius !== null && !currentPosition) {
      const status = controls!.querySelector<HTMLElement>("[data-status]");
      if (status) status.textContent = "Autorisez la géolocalisation pour filtrer les annonces proches de vous…";
      try {
        currentPosition = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(
            (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
            reject,
            { timeout: 10000, maximumAge: 300000 },
          );
        });
      } catch {
        const select = controls!.querySelector<HTMLSelectElement>("[data-radius]");
        if (select) select.value = "off";
        if (status) status.textContent = "Géolocalisation refusée ou indisponible.";
        return;
      }
    }

    let shown = 0;
    let matchedCards = 0;
    for (const req of requests) {
      const card = findCardForRequest(root, req);
      if (!card) continue;
      matchedCards++;
      const profile = profiles.get(req.parent_id);
      const nameOk = !name || norm(profile?.full_name).includes(name);
      const cityOk = city === "all" || norm(req.city) === city;
      const rate = req.proposed_rate == null ? null : Number(req.proposed_rate);
      const minOk = min == null || (rate != null && rate >= min);
      const maxOk = max == null || (rate != null && rate <= max);
      let geoOk = true;
      if (radius !== null) {
        geoOk = !!currentPosition && profile?.latitude != null && profile?.longitude != null &&
          haversineKm(currentPosition.lat, currentPosition.lng, profile.latitude, profile.longitude) <= radius;
      }
      const visible = nameOk && cityOk && minOk && maxOk && geoOk;
      card.style.display = visible ? "" : "none";
      if (visible) shown++;
    }

    const status = controls!.querySelector<HTMLElement>("[data-status]");
    if (status) status.textContent = matchedCards ? `${shown} annonce${shown > 1 ? "s" : ""} affichée${shown > 1 ? "s" : ""}` : "Les annonces sont en cours de chargement…";
  };

  controls.querySelectorAll("input,select").forEach((el) => {
    el.addEventListener(el.tagName === "INPUT" ? "input" : "change", () => { void apply(); });
  });
  controls.querySelector<HTMLButtonElement>("[data-reset]")?.addEventListener("click", () => {
    controls!.querySelectorAll<HTMLInputElement>("input").forEach((i) => { i.value = ""; });
    const citySelect = controls!.querySelector<HTMLSelectElement>("[data-city]");
    const radiusSelect = controls!.querySelector<HTMLSelectElement>("[data-radius]");
    if (citySelect) citySelect.value = "all";
    if (radiusSelect) radiusSelect.value = "off";
    void apply();
  });

  void apply();
  return controls;
}

async function refresh() {
  scheduled = false;
  if (window.location.pathname !== "/demandes") {
    document.getElementById("cg-public-request-filters")?.remove();
    return;
  }
  const page = findPageRoot();
  if (!page) return;
  if (!loaded) await loadData();
  buildControls(page.heading, page.root);
}

function scheduleRefresh() {
  if (scheduled) return;
  scheduled = true;
  requestAnimationFrame(() => { void refresh(); });
}

export function registerCareRequestPublicFilters() {
  if (typeof window === "undefined" || observer) return;
  const start = () => {
    scheduleRefresh();
    observer = new MutationObserver(scheduleRefresh);
    observer.observe(document.body, { childList: true, subtree: true });
    window.addEventListener("popstate", scheduleRefresh);
    window.addEventListener("hashchange", scheduleRefresh);
  };
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", start, { once: true });
  else start();
}
